// Shared helpers for the "Seleccion de bobinas" (CMT_INF) screen replica.

/**
 * Loose field getter: tolerant to casing differences between what the
 * backend DTOs actually serialize (mostly lower-case, except recId/dataAreaId)
 * and what a caller might expect.
 */
export const getFieldLoose = (source, fieldName) => {
  if (!source || typeof source !== "object" || !fieldName) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(source, fieldName) && source[fieldName] !== undefined) {
    return source[fieldName];
  }
  const lower = fieldName.toLowerCase();
  for (const [key, value] of Object.entries(source)) {
    if (key.toLowerCase() === lower && value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

export const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const isTrueFlag = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const text = String(value).trim().toLowerCase();
  return text === "1" || text === "true" || text === "si" || text === "sí";
};

export const formatDateTime = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value);
  }
};

export const formatDateOnly = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString();
  } catch {
    return String(value);
  }
};

export const formatNumber = (value, decimals = 2) => {
  const n = toNumberOrNull(value);
  if (n === null) return "";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};

// -----------------------------------------------------------------------
// Estado PV (SalesTable.cmtseleccionbobinasestadopv) - badge colors
// -----------------------------------------------------------------------

export const ESTADO_PV_CODES = {
  NO_ENVIADO: 0,
  ENVIADO: 1,
  REVISADO: 2,
  REENVIADO: 3,
};

export const ESTADO_PV_BADGE_STYLE = {
  0: { backgroundColor: "#e5e7eb", color: "#374151" }, // gris - No enviado
  1: { backgroundColor: "#bfdbfe", color: "#1e3a8a" }, // azul - Enviado
  2: { backgroundColor: "#bbf7d0", color: "#14532d" }, // verde - Revisado
  3: { backgroundColor: "#fed7aa", color: "#7c2d12" }, // naranja - Reenviado
};

export const estadoPvBadgeStyle = (code) => {
  const n = toNumberOrNull(code);
  return ESTADO_PV_BADGE_STYLE[n] || ESTADO_PV_BADGE_STYLE[0];
};

// Style/label for fields that come from AX4 -> IDAX sync but have not been
// populated yet for a given row (e.g. historical orders synced before this
// field was activated, or a line whose recid has not arrived yet). IMPORTANT:
// never silently coerce a missing/null value to a valid enum code (0 looks
// like a legitimate "No enviado"/"Si" and misleads the user into thinking the
// real AX state is known when it is not synced at all).
export const NOT_SYNCED_BADGE_STYLE = { backgroundColor: "#f3f4f6", color: "#9ca3af", border: "1px dashed #d1d5db" };
export const NOT_SYNCED_LABEL = "—"; // em dash

// -----------------------------------------------------------------------
// Candidate conflict color scheme (simplified vs the 13 colors of AX CMT_INF)
// -----------------------------------------------------------------------

export const CONFLICT_ROW_STYLE = {
  NONE: { backgroundColor: "#ffffff" },
  SELECTED_HERE: { backgroundColor: "#bbf7d0" }, // verde: seleccionado aqui, sin conflicto
  SELECTED_ELSEWHERE_FULL: { backgroundColor: "#bfdbfe" }, // azul: seleccionado en otra linea, completo
  SELECTED_ELSEWHERE_PARTIAL: { backgroundColor: "#fef08a" }, // amarillo: parcial en otra linea
};

export const CONFLICT_ROW_STYLE_SIMPLE = {
  NONE: { backgroundColor: "#ffffff" },
  SELECTED_HERE: { backgroundColor: "#ffffff", fontWeight: 600 },
  SELECTED_ELSEWHERE_FULL: { backgroundColor: "#ffffff", fontStyle: "italic" },
  SELECTED_ELSEWHERE_PARTIAL: { backgroundColor: "#ffffff", fontStyle: "italic" },
};

export const conflictRowStyle = (conflictCode, colorsDisabled) => {
  const table = colorsDisabled ? CONFLICT_ROW_STYLE_SIMPLE : CONFLICT_ROW_STYLE;
  return table[conflictCode] || table.NONE;
};

export const TIPO_CODES = {
  DISPONIBLE_FISICO: "DISPONIBLE_FISICO",
  PENDIENTE_RECIBIR: "PENDIENTE_RECIBIR",
};
